(() => {
  'use strict';
  const configNode = document.getElementById('simal-purchase-config');
  if (!configNode || window.simalPurchaseReady) return;
  window.simalPurchaseReady = true;
  const config = JSON.parse(configNode.textContent);
  const root = config.root.endsWith('/') ? config.root : config.root + '/';
  const busy = new Set();
  const checkoutPass = new WeakSet();
  let statusTimer;
  const isRoll = id => Boolean(config.rolls[String(id)]);
  const isFiscal = id => Boolean(config.fiscal[String(id)]);
  const handlesPurchase = ctx => isRoll(ctx.id) ||
    (isFiscal(ctx.id) && document.body.classList.contains('template-index') &&
      Boolean(ctx.control.closest('.fc-products')));
  const stop = event => { event.preventDefault(); event.stopImmediatePropagation(); };

  function announce(message, persistent = false) {
    let node = document.getElementById('simal-purchase-status');
    if (!node) {
      node = document.createElement('div');
      node.id = 'simal-purchase-status';
      node.className = 'simal-purchase-status';
      node.setAttribute('role', 'status');
      node.setAttribute('aria-live', 'polite');
      document.body.append(node);
    }
    node.textContent = message;
    node.hidden = false;
    clearTimeout(statusTimer);
    if (!persistent) statusTimer = setTimeout(() => { node.hidden = true; }, 5000);
  }

  function context(control) {
    let form = control.form || control.closest('form');
    const card = control.closest('.product-item, .fc-card, [data-js-product]');
    if (!form && card) form = card.querySelector('form');
    const idInput = form?.querySelector('[name="id"], [name="variant_id"]');
    const id = idInput?.value || control.dataset.pid;
    const quantityInput = form?.querySelector('[name="quantity"]') || card?.querySelector('[name="quantity"]');
    return { form, id, quantityInput, control };
  }

  function quantityId(input) {
    if (input.name === 'updates[]') {
      return input.dataset.productId || input.id.replace(/^updates_/, '').split(':')[0];
    }
    return context(input).id;
  }

  function setupQuantities(scope = document) {
    scope.querySelectorAll('[name="quantity"], [name="updates[]"]').forEach(input => {
      if (!isRoll(quantityId(input)) || input.dataset.simalReady) return;
      input.dataset.simalReady = '1';
      input.min = '10';
      input.step = '10';
      input.dataset.min = '10';
      input.dataset.step = '10';
      if (input.type !== 'hidden') input.type = 'number';
      if (input.name === 'quantity' && Number(input.value) < 10) input.value = '10';
      if (input.type !== 'hidden' && !input.closest('form')?.querySelector('.simal-quantity-note')) {
        const note = document.createElement('p');
        note.className = 'simal-quantity-note';
        note.textContent = config.text.quantity;
        const wrapper = input.closest('.fc-qty, .js-quantity, .quantity');
        (wrapper || input).insertAdjacentElement('afterend', note);
      }
    });
  }

  function validQuantity(input) {
    const value = Number(input.value);
    const valid = Number.isInteger(value) && value >= 10 && value % 10 === 0;
    input.setCustomValidity(valid ? '' : config.text.quantity);
    if (!valid) { announce(config.text.quantity, true); input.reportValidity(); }
    return valid;
  }

  async function request(path, options) {
    const response = await fetch(root + path, { credentials: 'same-origin', ...options });
    const json = await response.json();
    if (!response.ok || json.status >= 400) throw new Error(json.description || json.message || config.text.error);
    return json;
  }

  async function add(ctx) {
    const key = ctx.form || ctx.control;
    if (busy.has(key) || ctx.control.matches('[disabled]')) return;
    setupQuantities();
    if (isRoll(ctx.id) && ctx.quantityInput && !validQuantity(ctx.quantityInput)) return;
    const quantity = ctx.quantityInput ? Number(ctx.quantityInput.value) : (isRoll(ctx.id) ? 10 : 1);
    if (!Number.isInteger(quantity) || quantity < 1) { announce(config.text.error); return; }
    if (ctx.form && !ctx.form.reportValidity()) return;
    const item = { id: Number(ctx.id), quantity };
    if (ctx.form) {
      const data = new FormData(ctx.form);
      const properties = {};
      for (const [name, value] of data) {
        const match = name.match(/^properties\[(.+)\]$/);
        if (match && typeof value === 'string') properties[match[1]] = value;
      }
      if (Object.keys(properties).length) item.properties = properties;
      if (data.get('selling_plan')) item.selling_plan = data.get('selling_plan');
    }
    const items = [item];
    if (isFiscal(ctx.id)) {
      const serviceBox = ctx.form?.querySelector('[data-fc-services]');
      if (serviceBox) {
        const controls = [...serviceBox.querySelectorAll('[data-fc-service]')];
        if (controls.some(input => input.dataset.locked === '1' && input.disabled)) {
          announce(config.text.serviceUnavailable, true); return;
        }
        const serviceQty = serviceBox.hasAttribute('data-fc-services-follow-qty') ? quantity : 1;
        for (const input of controls) {
          if (!input.disabled && (input.checked || input.dataset.locked === '1')) {
            items.push({ id: Number(input.dataset.fcService), quantity: serviceQty });
          }
        }
      } else {
        if (config.services.some(service => !service.id || !service.available)) {
          announce(config.text.serviceUnavailable, true); return;
        }
        for (const service of config.services) items.push({ id: service.id, quantity });
      }
    }
    busy.add(key);
    const button = ctx.control;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('ajax_loading', 'is-busy');
    try {
      const response = await request('cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: ['minicart'], sections_url: location.pathname })
      });
      if (response.sections?.minicart) {
        document.body.dispatchEvent(new CustomEvent('drawer:Minicart', { detail: { response } }));
        document.body.dispatchEvent(new CustomEvent('ajax:addToCart', { detail: { response } }));
        const opener = document.querySelector('.push_side[data-id="#js_cart_popup"]');
        if (opener && !document.documentElement.classList.contains('pside_opened')) opener.click();
      } else {
        document.body.dispatchEvent(new CustomEvent('update:miniCart', { bubbles: true }));
      }
      button.classList.add('is-added');
      setTimeout(() => button.classList.remove('is-added'), 1800);
      announce(config.text.added);
    } catch (error) {
      announce(error.message || config.text.error, true);
    } finally {
      busy.delete(key);
      button.removeAttribute('aria-busy');
      button.classList.remove('ajax_loading', 'is-busy');
    }
  }

  async function checkCheckout(control) {
    if (busy.has(control)) return;
    busy.add(control);
    try {
      const cart = await request('cart.js', { headers: { Accept: 'application/json' } });
      if (cart.items.some(item => isRoll(item.variant_id) && (item.quantity < 10 || item.quantity % 10 !== 0))) {
        announce(config.text.cartInvalid, true);
        return;
      }
      checkoutPass.add(control);
      control.click();
      checkoutPass.delete(control);
    } catch (error) {
      announce(error.message || config.text.error, true);
    } finally { busy.delete(control); }
  }

  window.addEventListener('click', event => {
    const target = event.target.closest?.('button, a, input[type="submit"]');
    if (!target) return;
    if (target.matches('[name="checkout"], a[href*="/checkout"]')) {
      if (checkoutPass.has(target)) return;
      if (target.disabled) return;
      stop(event);
      checkCheckout(target);
      return;
    }
    if (target.matches('[data-fc-qty], .js_plus, .js_minus')) {
      const input = target.closest('.fc-qty, .js-quantity')?.querySelector('input');
      if (!input || !isRoll(quantityId(input))) return;
      stop(event);
      const plus = target.matches('.js_plus, [data-fc-qty="1"]');
      const current = Number(input.value) || 10;
      const next = plus ? (Math.floor(current / 10) + 1) * 10 : Math.max(10, (Math.ceil(current / 10) - 1) * 10);
      if (next === current) return;
      input.value = String(next);
      input.setCustomValidity('');
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const isAdd = target.matches('.js_add_to_cart_button') ||
      (target.matches('button[type="submit"], input[type="submit"]') && target.closest('form[action*="/cart/add"]'));
    if (!isAdd) return;
    const ctx = context(target);
    if (!handlesPurchase(ctx)) return;
    stop(event);
    add(ctx);
  }, true);

  window.addEventListener('submit', event => {
    if (event.target.matches('form[action*="/cart/add"]')) {
      const button = event.submitter || event.target.querySelector('[type="submit"]');
      const ctx = context(button || event.target);
      if (!handlesPurchase(ctx)) return;
      stop(event);
      add(ctx);
    } else if (event.target.matches('form[action$="/cart"]')) {
      const inputs = [...event.target.querySelectorAll('[name="updates[]"]')].filter(input => isRoll(quantityId(input)));
      if (inputs.some(input => !validQuantity(input))) { stop(event); return; }
      const button = event.submitter;
      if (button?.name === 'checkout' && !checkoutPass.has(button)) {
        stop(event);
        checkCheckout(button);
      }
    }
  }, true);

  window.addEventListener('change', event => {
    const input = event.target;
    if (!input.matches('[name="quantity"], [name="updates[]"]') || !isRoll(quantityId(input))) return;
    if (!validQuantity(input)) stop(event);
  }, true);
  window.addEventListener('input', event => {
    const input = event.target;
    if (input.dataset.simalReady) input.setCustomValidity('');
  }, true);
  setupQuantities();
  let scheduled = false;
  new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; setupQuantities(); });
  }).observe(document.body, { childList: true, subtree: true });
})();
